import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { extractErrorMessage } from '../../api/errors';
import { kioskStatus, newKioskCode, type KioskCode, type KioskStatus } from '../../api/join';

/** How often the kiosk checks whether its code has been taken. */
export const KIOSK_POLL_MS = 2000;
/** Fetch a fresh code this long before the shown one would lapse. */
const RENEW_BEFORE_MS = 30_000;

/** One id per kiosk browser, so two laptops at a booth show their own codes. */
function kioskId(): string {
  const key = 'booth-kiosk-id';
  try {
    const have = localStorage.getItem(key);
    if (have) return have;
    const made = `kiosk-${crypto.randomUUID()}`;
    localStorage.setItem(key, made);
    return made;
  } catch {
    return `kiosk-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

/**
 * The booth laptop as a kiosk (BOOTH-SELF-ENROL): a large QR code that a
 * participant scans to enroll on their own phone. The code is single-use — as
 * soon as a phone opens it, the next one appears — so the booth handles as
 * many people at once as it has visitors, and nobody has to operate the
 * laptop. "Fill in here instead" opens the classic on-laptop flow for anyone
 * without a phone.
 */
export function BoothKiosk({
  open,
  tenantId,
  onClose,
  onFillInHere,
  onActivity,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onFillInHere: () => void;
  /** Someone enrolled — the participant list behind can refresh. */
  onActivity?: () => void;
}) {
  const [code, setCode] = useState<KioskCode | null>(null);
  const [status, setStatus] = useState<KioskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef<string>('');
  const doneRef = useRef<number>(0);

  const fresh = useCallback(async () => {
    try {
      if (!idRef.current) idRef.current = kioskId();
      setCode(await newKioskCode(tenantId, idRef.current));
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'could not get a code — retrying'));
    }
  }, [tenantId]);

  useEffect(() => {
    if (!open) return;
    void fresh();
    return () => setCode(null);
  }, [open, fresh]);

  useEffect(() => {
    if (!open || !code) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const s = await kioskStatus(tenantId, code.code_id);
        if (cancelled) return;
        setStatus(s);
        if (s.done_today !== doneRef.current) {
          doneRef.current = s.done_today;
          onActivity?.();
        }
        const lapsing = new Date(code.expires_at).getTime() - Date.now() < RENEW_BEFORE_MS;
        if (s.state !== 'open' || lapsing) await fresh();
      } catch {
        // A blip: keep showing the code; the next tick tries again.
      }
    }, KIOSK_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, code, tenantId, fresh, onActivity]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen aria-labelledby="booth-kiosk-title">
      <Toolbar sx={{ borderBottom: 1, borderColor: 'divider', gap: 2 }}>
        <Typography id="booth-kiosk-title" variant="h6" component="h1" sx={{ flex: 1 }}>
          Enroll at booth
        </Typography>
        <Button onClick={onFillInHere}>Fill in on this laptop instead</Button>
        <IconButton edge="end" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ flex: 1, p: { xs: 2, md: 4 } }}
      >
        <Typography variant="h3" component="h2">
          Scan to enroll
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Point your phone’s camera at the code — it takes about three minutes.
        </Typography>
        <Box
          sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: 3, lineHeight: 0 }}
          aria-label="Enrollment QR code"
          role="img"
        >
          {code ? (
            <QRCodeSVG value={code.url} size={340} level="M" data-testid="kiosk-qr" />
          ) : (
            <Box sx={{ width: 340, height: 340 }} />
          )}
        </Box>
        {error && <Alert severity="warning">{error}</Alert>}
        <Stack direction="row" spacing={2}>
          <Chip label={`${status?.in_progress ?? 0} filling in now`} />
          <Chip color="success" label={`${status?.done_today ?? 0} enrolled today`} />
        </Stack>
        {status && status.recent.length > 0 && (
          <Stack spacing={0.5}>
            <Typography variant="overline" color="text.secondary">
              Just enrolled
            </Typography>
            {status.recent.map((r) => (
              <Typography key={`${r.at}-${r.name ?? ''}`} variant="body1">
                {r.name ?? 'Someone'}
                {r.possible_duplicate && (
                  <Chip size="small" color="warning" label="possible duplicate" sx={{ ml: 1 }} />
                )}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
